import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import type { Tenant } from "../../../shared/types";
import { useAuth } from "../../auth/AuthContext";
import { Boton } from "../../components/Boton";
import { Cargando } from "../../components/Cargando";
import { EncabezadoPagina } from "../../components/EncabezadoPagina";
import { Campo, Input } from "../../components/Formulario";
import { Icono } from "../../components/Icono";
import { Tarjeta } from "../../components/Tarjeta";
import { useToast } from "../../components/Toast";
import { api, ErrorApi } from "../../lib/apiClient";
import { PanelCatalogoEstudio } from "./PanelCatalogoEstudio";
import { PanelCategoriasEconomicas } from "./PanelCategoriasEconomicas";
import { PanelColorCatalogo } from "./PanelColorCatalogo";
import { PanelDistribucionGastos } from "./PanelDistribucionGastos";
import { PanelIdentidadEstudio } from "./PanelIdentidadEstudio";
import { PanelObjetivosConfiguracion } from "./PanelObjetivosConfiguracion";
import { PanelValoresJus } from "./PanelValoresJus";

export function ConfiguracionPage() {
  const { mostrar } = useToast();
  const { permisos } = useAuth();
  const veCatalogos = permisos.has("catalogos_estudio.ver");
  const administraCatalogos = permisos.has("catalogos_estudio.administrar");
  const veCategoriasEconomicas = permisos.has("economia.ver");
  const administraCategoriasEconomicas = permisos.has("economia.administrar_catalogos");
  const administraValoresJus = permisos.has("economia.configurar_jus");
  const administraProcesos = permisos.has("procesos.administrar");
  const puedeEditar = permisos.has("configuracion.editar");
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [valores, setValores] = useState({ nombre: "", direccion: "", telefono: "", email_contacto: "" });
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    api
      .get<{ tenant: Tenant }>("/configuracion")
      .then((d) => {
        setTenant(d.tenant);
        setValores({
          nombre: d.tenant.nombre,
          direccion: d.tenant.direccion ?? "",
          telefono: d.tenant.telefono ?? "",
          email_contacto: d.tenant.email_contacto ?? "",
        });
      })
      .catch(() => mostrar("No se pudo cargar la configuración.", "error"));
  }, [mostrar]);

  async function manejarEnvio(e: FormEvent) {
    e.preventDefault();
    setGuardando(true);
    try {
      const d = await api.put<{ tenant: Tenant }>("/configuracion", valores);
      setTenant(d.tenant);
      mostrar("Configuración guardada.", "exito");
    } catch (err) {
      mostrar(
        err instanceof ErrorApi
          ? err.message
          : "No se pudo guardar la configuración.",
        "error",
      );
    } finally {
      setGuardando(false);
    }
  }

  if (!tenant) return <Cargando />;

  return (
    <div>
      <EncabezadoPagina
        titulo="Configuración"
        descripcion="Datos generales del estudio"
      />
      <Tarjeta className="max-w-lg p-5">
        <form onSubmit={manejarEnvio} className="space-y-4">
          <Campo etiqueta="Nombre del estudio" obligatorio>
            <Input
              required
              value={valores.nombre}
              onChange={(e) => setValores((v) => ({ ...v, nombre: e.target.value }))}
            />
          </Campo>
          <Campo etiqueta="Dirección">
            <Input
              value={valores.direccion}
              onChange={(e) => setValores((v) => ({ ...v, direccion: e.target.value }))}
            />
          </Campo>
          <div className="grid grid-cols-2 gap-4">
            <Campo etiqueta="Teléfono">
              <Input
                value={valores.telefono}
                onChange={(e) => setValores((v) => ({ ...v, telefono: e.target.value }))}
              />
            </Campo>
            <Campo etiqueta="Email de contacto">
              <Input
                type="email"
                value={valores.email_contacto}
                onChange={(e) => setValores((v) => ({ ...v, email_contacto: e.target.value }))}
              />
            </Campo>
          </div>
          <p className="text-xs text-texto-secundario">
            Identificador interno del estudio:{" "}
            <span className="font-mono">{tenant.slug}</span>
          </p>
          <Boton type="submit" cargando={guardando}>
            Guardar cambios
          </Boton>
        </form>
      </Tarjeta>

      <Tarjeta className="mt-6 max-w-lg p-5">
        <PanelIdentidadEstudio tenant={tenant} puedeEditar={puedeEditar} onCambio={setTenant} />
      </Tarjeta>

      <Tarjeta className="mt-6 max-w-lg p-5">
        <PanelObjetivosConfiguracion puedeEditar={puedeEditar} />
      </Tarjeta>

      {administraProcesos && (
        <Tarjeta className="mt-6 max-w-lg p-5">
          <Link to="/configuracion/procesos" className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secundario/10 text-secundario">
              <Icono nombre="kanban" className="h-5 w-5" />
            </span>
            <div>
              <p className="text-sm font-semibold text-texto">Procesos de Trabajo</p>
              <p className="text-xs text-texto-secundario">
                Configurar los flujos internos del estudio (Procesos y Etapas) usados en el Kanban.
              </p>
            </div>
          </Link>
        </Tarjeta>
      )}

      {veCatalogos && (
        <Tarjeta className="mt-6 space-y-6 p-5">
          <PanelCatalogoEstudio
            titulo="Estados procesales"
            descripcion="Etapas del trámite que se pueden asignar a un expediente en la sección Evaluación y estrategia."
            endpoint="estados-procesales"
            puedeAdministrar={administraCatalogos}
            mostrarColor
            paletaControlada
          />
          <PanelCatalogoEstudio
            titulo="Valores estratégicos"
            descripcion="Etiquetas de valor estratégico que se pueden asignar a un expediente."
            endpoint="valores-estrategicos"
            puedeAdministrar={administraCatalogos}
          />
          <PanelCatalogoEstudio
            titulo="Unidades de Negocio"
            descripcion="Centros de resultado internos del estudio (Laboral, ART, Empresas...), independientes de la Materia jurídica. Cada expediente pertenece a una Unidad; cada usuario ve sólo las unidades que tiene asignadas."
            endpoint="areas-practica"
            puedeAdministrar={administraCatalogos}
            mostrarColor
            mostrarResponsable
            mostrarDescripcion
          />
          <PanelDistribucionGastos puedeAdministrar={administraCatalogos} />
          <PanelCatalogoEstudio
            titulo="Tipos de actuación"
            descripcion="Tipos elegibles al registrar una actuación (Escrito, Audiencia, Presentación...)."
            endpoint="tipos-actuacion"
            puedeAdministrar={administraCatalogos}
          />
          <PanelCatalogoEstudio
            titulo="Tipos de honorario"
            descripcion="Tipos elegibles al registrar un honorario (Primera Instancia, Alzada, Mediación...)."
            endpoint="tipos-honorario"
            puedeAdministrar={administraCatalogos}
          />
          <PanelColorCatalogo
            titulo="Color de Materias"
            descripcion="Color con el que este estudio identifica cada Materia jurídica en dashboards y listados. Las submaterias heredan el color de su materia."
            tipo="materia"
            puedeAdministrar={administraCatalogos}
          />
          <PanelColorCatalogo
            titulo="Color de Fueros"
            descripcion="Color con el que este estudio identifica cada Fuero. Los organismos heredan el color de su fuero."
            tipo="fuero"
            puedeAdministrar={administraCatalogos}
          />
        </Tarjeta>
      )}

      {veCategoriasEconomicas && (
        <Tarjeta className="mt-6 p-5">
          <PanelCategoriasEconomicas puedeAdministrar={administraCategoriasEconomicas} />
        </Tarjeta>
      )}

      {veCategoriasEconomicas && (
        <Tarjeta className="mt-6 p-5">
          <PanelValoresJus puedeAdministrar={administraValoresJus} />
        </Tarjeta>
      )}
    </div>
  );
}
